/* eslint-disable @typescript-eslint/member-ordering */
import {Component, Input, OnDestroy} from '@angular/core';
import {Store} from '@ngrx/store';
import {killColleaguesService} from '../../../core/store-shared/shared.actions';
import {IColleagues, IColleagueUser, IGroupAccess, IGroupAccessPerStore, IUser, IUserAccess} from '../../models';
import {IStore} from '../../../../shared/shared-models/store/store';
import {AddUserComponent} from '../add-user/add-user.component';
import {
  StaffAccessPasswordResetLinkModalComponent,
} from '../staff-access-password-reset-link-modal/staff-access-password-reset-link-modal.component';
import {
  IStaffAccessPasswordResetLink,
} from '../../service/staff-access-password-reset-link/staff-access-password-reset-link.service';
import {
  CustomisePermissionsModalComponent,
  ICustomisePermissionsModalResult,
} from '../customise-permissions-modal/customise-permissions-modal.component';
import {
  IStaffAccessUserChange,
  StaffAccessSaveChangesModalComponent,
  IStaffAccessSaveChangesModalResult,
} from '../staff-access-save-changes-modal/staff-access-save-changes-modal.component';
import {getDeepCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';
import {
  clearLastFailedStaffAccessCreateUserId,
  setColleaguesUserAccess,
} from '../../user-access-store/user-access.actions';
import {AccessCodeBitEnum, USER_ROLL_DESCRIPTION} from '../../models/access-code.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  IUserProfileConfigDismissData,
  UserProfileConfigComponent,
} from '../../../../shared-modules/shared-module/components/user-profile-config/user-profile-config.component';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {setAreChangesSaved} from '../../user-stores-store/user-store.actions';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {RuleHumanID} from '../../models/rule-info.enum';
import {
  accessCodeFromRuleHumanIds,
  findBestMatchingGroupName,
  getPresetRoleNames,
  isCustomised,
  roleDefaultRuleIds,
  ruleHumanIdsFromAccessCode,
} from '../../utils/custom-permissions.utils';
import {IUserAccessOperationProgress} from '../../models/user-access-operation-progress.interface';
import {formatLastSeenDaysAgo} from '../../utils/user-access-date.utils';

@Component({
  selector: 'app-user-access',
  templateUrl: './user-access.page.html',
  styleUrls: ['./user-access.page.scss'],
  standalone: false,
})
export class UserAccessPage implements OnDestroy {
  currentUser: IUser;
  colleagues: IColleagues = {};
  colleaguesCompare: IColleagues = {};
  stores: Record<string, string> = {};
  currentUserAccess: IUserAccess = {} as IUserAccess;

  colleaguesSorted: string[] = [];
  storesOrder: string[] = [];
  usersOrder: string[] = [];
  colleaguesStoresSorted: Record<string, string[]> = {};
  storeUsers: Record<string, string[]> = {};
  fuzzyUsers: string[] = [];
  fuzzyStores: string[] = [];
  selectedAccordion: string | null = null;
  searchString = '';
  byStore = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MAX_USER_COUNT: number = 20;
  defaultUserAccessCodes: IGroupAccess;
  isLoading = false;
  loadingPresentation: IUserAccessOperationProgress | null = null;
  colleagueAccessGroups: Record<string, Record<string, string>> = {};
  accessGroupNames: string[] = [];
  editedPermissions: Record<string, IGroupAccessPerStore> = {};
  customRuleSelections: Record<string, Record<string, RuleHumanID[]>> = {};
  deletedCustomSelections: Record<string, Record<string, RuleHumanID[]>> = {};
  private customRoleSwitchStash: Record<string, Record<string, RuleHumanID[]>> = {};
  haveUsersOrStoresBeenUpdated = false;
  deletedItems: Record<string, Record<string, string>> = {};
  private readonly pendingCreatedUserEmails = new Set<string>();
  protected readonly icon = IconsEnum;
  protected readonly object = Object;
  protected readonly userGroupDescription = USER_ROLL_DESCRIPTION;
  protected readonly accessCode = AccessCodeBitEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly adminPermissionsTooltip =
    'Admin users have full access. Permissions cannot be customised for the Admin role.';
  storeSearchString: string;

  constructor(
    private modalController: ModalControllerService,
    private readonly store: Store,
  ) {
  }

  get getUserStoreCount(): string {
    if (this.byStore) {
      return `${(this.storeUsers?.[this.selectedAccordion]?.length || 0) + 1}/${this.MAX_USER_COUNT} Users`;
    } else {
      return `${this.colleagues?.[this.selectedAccordion]?.storeList?.length || 0}/${this.MAX_USER_COUNT} Stores`;
    }
  }

  @Input() set setCurrentUser(user: IUser) {
    this.currentUser = {...user};
  }

  @Input() set setCurrentUserAccess(userAccess: IUserAccess) {
    this.currentUserAccess = getDeepCopyOfObject(userAccess);
  }

  @Input() set setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @Input() set setLoadingPresentation(progress: IUserAccessOperationProgress | null) {
    this.loadingPresentation = progress;
  }

  @Input() set setLastFailedStaffAccessCreateUserId(userId: string | null) {
    if (!userId) {
      return;
    }
    this.removePendingColleaguePlaceholder(userId);
    this.store.dispatch(clearLastFailedStaffAccessCreateUserId());
  }

  @Input() set setGroupAccess(groupAccess: IGroupAccess) {
    this.defaultUserAccessCodes = groupAccess;
    if (!this.hasGroupAccessData()) {
      return;
    }
    this.accessGroupNames = getPresetRoleNames(Object.keys(this.defaultUserAccessCodes));
    if (!this.haveUsersOrStoresBeenUpdated) {
      this.populateColleagueAccessGroups();
    } else {
      this.reapplyPendingEdits();
    }
  }

  /**
   * Applies colleague state from the store.
   *
   * Incoming state is always authoritative for store assignments; unsaved edits are tracked separately in
   * `editedPermissions` and `deletedItems` and are layered back on top by `reapplyPendingEdits`.
   *
   * @param {IColleagues} colleagues - Colleague state emitted by the store.
   */
  @Input() set setColleaguesFromState(colleagues: IColleagues) {
    const incoming = getDeepCopyOfObject(colleagues);

    if (!this.haveUsersOrStoresBeenUpdated) {
      this.colleagues = incoming;
      this.colleaguesCompare = getDeepCopyOfObject(colleagues);
      this.colleagueAccessGroups = {};
      this.customRuleSelections = {};
      this.deletedCustomSelections = {};
      this.customRoleSwitchStash = {};
      this.populateColleagueAccessGroups();
    } else {
      const incomingCompare = getDeepCopyOfObject(colleagues);
      for (const userId of Object.keys(incoming)) {
        this.colleagues[userId] = incoming[userId];
        this.colleaguesCompare[userId] = incomingCompare[userId];
      }
      this.populateColleagueAccessGroups();
      this.reapplyPendingEdits();
    }

    this.sortColleagues();
    this.sortStores();
    this.populateStoreUsers();
    this.applyFilters(this.searchString);
    this.syncPendingChangesFlag();
  }

  @Input() set setStoresFromState(stores: IStore[]) {
    this.stores = {};
    stores.forEach((store: IStore) => {
      this.stores[store.storeId] = store.name;
    });
    this.storesOrder = Object.keys(this.stores);
    this.sortStores();
  }

  ngOnDestroy(): void {
    this.store.dispatch(killColleaguesService());
  }

  private hasGroupAccessData(): boolean {
    return !!this.defaultUserAccessCodes && Object.keys(this.defaultUserAccessCodes).length > 0;
  }

  populateColleagueAccessGroups(): void {
    if (!this.colleagues || !this.hasGroupAccessData()) {
      return;
    }

    this.accessGroupNames = getPresetRoleNames(Object.keys(this.defaultUserAccessCodes));
    this.accessGroupNames.sort((a: string, b: string) => {
      const priorityA = this.defaultUserAccessCodes[a]?.orderPriority ?? Infinity;
      const priorityB = this.defaultUserAccessCodes[b]?.orderPriority ?? Infinity;
      return priorityA - priorityB;
    });

    for (const userId of Object.keys(this.colleagues)) {
      if (!this.colleagueAccessGroups[userId]) this.colleagueAccessGroups[userId] = {};
      const storeIds = Object.keys(this.colleagues[userId].stores || {});
      for (const storeId of storeIds) {
        if (this.editedPermissions[userId]?.[storeId] || this.deletedItems[userId]?.[storeId]) {
          continue;
        }

        const accessCodeVal = this.colleagues[userId].stores[storeId];
        if (accessCodeVal === null || accessCodeVal === undefined) {
          continue;
        }

        let groupName = this.getGroupNameByAccessCode(accessCodeVal);
        const decodedRuleIds = ruleHumanIdsFromAccessCode(accessCodeVal);

        if (groupName === null) {
          groupName = findBestMatchingGroupName(accessCodeVal, this.defaultUserAccessCodes)
            ?? this.accessGroupNames[0]
            ?? 'Stock Viewer';
          if (!this.customRuleSelections[userId]) this.customRuleSelections[userId] = {};
          this.customRuleSelections[userId][storeId] = decodedRuleIds;
          this.colleagueAccessGroups[userId][storeId] = 'Custom';
        } else if (isCustomised(groupName, decodedRuleIds, this.defaultUserAccessCodes)) {
          if (!this.customRuleSelections[userId]) this.customRuleSelections[userId] = {};
          this.customRuleSelections[userId][storeId] = decodedRuleIds;
          this.colleagueAccessGroups[userId][storeId] = 'Custom';
        } else {
          this.colleagueAccessGroups[userId][storeId] = groupName;
        }
      }
    }
  }

  get presetRoleNames(): string[] {
    return getPresetRoleNames(this.accessGroupNames.length ? this.accessGroupNames : Object.keys(this.defaultUserAccessCodes ?? {}));
  }

  getRoleSelectOptions(userId: string, storeId: string): Record<string, ISelectOption> {
    const selectedGroup = this.colleagueAccessGroups[userId]?.[storeId];
    const options: Record<string, ISelectOption> = {};

    for (const groupName of this.presetRoleNames) {
      options[groupName] = {
        name: groupName,
        disabled: false,
        selected: selectedGroup === groupName,
      };
    }

    options.Custom = {
      name: 'Custom',
      disabled: false,
      selected: selectedGroup === 'Custom',
    };

    return options;
  }

  isAdminRole(userId: string, storeId: string): boolean {
    return this.colleagueAccessGroups[userId]?.[storeId] === 'Admin';
  }

  canAddStoreToSelectedUser(): boolean {
    if (!this.selectedAccordion || this.byStore) {
      return false;
    }
    const assignedStores = new Set(this.colleagues[this.selectedAccordion]?.storeList ?? []);
    return this.currentUserAccess.adminAccessStores.some((storeId: string) => {
      if (assignedStores.has(storeId)) {
        return false;
      }
      return this.getAvailableStoreSlots(storeId) > 0;
    });
  }

  /**
   * Whether the selected store can accept another user.
   * Creating a new user is allowed even when every existing colleague already has this store.
   */
  canAddUserToSelectedStore(): boolean {
    if (!this.selectedAccordion || !this.byStore) {
      return false;
    }
    return !this.isStoreAtUserCapacity();
  }

  /**
   * Whether any existing colleague can still be granted access to the selected store.
   */
  private hasUnassignedColleagueForSelectedStore(): boolean {
    if (!this.selectedAccordion || !this.byStore) {
      return false;
    }
    const assignedUsers = new Set(this.storeUsers[this.selectedAccordion] ?? []);
    return Object.keys(this.colleagues).some((userId: string) => {
      if (assignedUsers.has(userId)) {
        return false;
      }
      const userStoreCount = this.colleagues[userId]?.storeList?.length ?? 0;
      return this.MAX_USER_COUNT - 1 - userStoreCount > 0;
    });
  }

  isStoreAtUserCapacity(): boolean {
    if (!this.selectedAccordion || !this.byStore) {
      return false;
    }
    const userCount = this.storeUsers[this.selectedAccordion]?.length ?? 0;
    return userCount >= this.MAX_USER_COUNT - 1;
  }

  private getAvailableStoreSlots(storeId: string): number {
    let userCount = 0;
    for (const userId of Object.keys(this.colleagues)) {
      if (this.colleagues[userId]?.stores && storeId in this.colleagues[userId].stores) {
        userCount++;
      }
    }
    return this.MAX_USER_COUNT - 1 - userCount;
  }

  isAccessRowModified(userId: string, storeId: string): boolean {
    if (this.editedPermissions[userId]?.[storeId] || this.deletedItems[userId]?.[storeId]) {
      return true;
    }
    const savedAccessCode = this.colleaguesCompare[userId]?.stores?.[storeId];
    const currentAccessCode = this.colleagues[userId]?.stores?.[storeId];
    return savedAccessCode !== currentAccessCode;
  }

  isAccessRowCustomised(userId: string, storeId: string): boolean {
    if (this.isAdminRole(userId, storeId)) {
      return false;
    }
    if (this.colleagueAccessGroups[userId]?.[storeId] === 'Custom') {
      return true;
    }
    const groupName = this.colleagueAccessGroups[userId]?.[storeId];
    const customIds = this.customRuleSelections[userId]?.[storeId];
    if (!groupName || !customIds?.length) {
      return false;
    }
    return isCustomised(groupName, customIds, this.defaultUserAccessCodes);
  }

  onRoleSelectChange(
    userId: string,
    storeId: string,
    selected: Record<string, ISelectOption>,
  ): void {
    const groupName = Object.keys(selected).find((key) => selected[key].selected);
    if (!groupName) {
      return;
    }

    const previousGroupName = this.colleagueAccessGroups[userId]?.[storeId];
    if (groupName === previousGroupName) {
      if (groupName === 'Custom' && !this.customRuleSelections[userId]?.[storeId]?.length) {
        this.ensureCustomRuleSelectionsForStore(userId, storeId, previousGroupName);
        this.onAccessGroupChange(userId, storeId);
      }
      return;
    }

    this.colleagueAccessGroups[userId][storeId] = groupName;

    if (groupName === 'Custom') {
      this.ensureCustomRuleSelectionsForStore(userId, storeId, previousGroupName);
    } else {
      this.stashCustomRulesWhenLeavingCustom(userId, storeId, previousGroupName);
      if (this.customRuleSelections[userId]) {
        delete this.customRuleSelections[userId][storeId];
        if (Object.keys(this.customRuleSelections[userId]).length === 0) {
          delete this.customRuleSelections[userId];
        }
      }
    }
    this.onAccessGroupChange(userId, storeId);
  }

  private stashCustomRulesWhenLeavingCustom(
    userId: string,
    storeId: string,
    previousGroupName: string | undefined,
  ): void {
    if (previousGroupName !== 'Custom') {
      return;
    }
    const rules = this.customRuleSelections[userId]?.[storeId];
    if (!rules?.length) {
      return;
    }
    if (!this.customRoleSwitchStash[userId]) {
      this.customRoleSwitchStash[userId] = {};
    }
    this.customRoleSwitchStash[userId][storeId] = [...rules];
  }

  private ensureCustomRuleSelectionsForStore(
    userId: string,
    storeId: string,
    previousGroupName: string | undefined,
  ): void {
    if (!this.customRuleSelections[userId]) {
      this.customRuleSelections[userId] = {};
    }

    const existing = this.customRuleSelections[userId][storeId];
    if (existing?.length) {
      return;
    }

    const stashed = this.customRoleSwitchStash[userId]?.[storeId];
    if (stashed?.length) {
      this.customRuleSelections[userId][storeId] = [...stashed];
      delete this.customRoleSwitchStash[userId][storeId];
      if (Object.keys(this.customRoleSwitchStash[userId]).length === 0) {
        delete this.customRoleSwitchStash[userId];
      }
      return;
    }

    const savedCode = this.colleaguesCompare[userId]?.stores?.[storeId] ?? this.colleagues[userId]?.stores?.[storeId];
    if (savedCode) {
      const decoded = ruleHumanIdsFromAccessCode(savedCode);
      if (decoded.length) {
        this.customRuleSelections[userId][storeId] = decoded;
        return;
      }
    }

    if (previousGroupName && previousGroupName !== 'No Access' && previousGroupName !== 'Custom') {
      this.customRuleSelections[userId][storeId] = roleDefaultRuleIds(
        previousGroupName,
        this.defaultUserAccessCodes,
      );
      return;
    }

    this.customRuleSelections[userId][storeId] = [];
  }

  async onCustomisePermissionsClick(userId: string, storeId: string): Promise<void> {
    if (this.isDeleted(userId, storeId) || this.isAdminRole(userId, storeId)) {
      return;
    }

    const groupName = this.colleagueAccessGroups[userId]?.[storeId];
    if (!groupName) {
      return;
    }

    const existingCustom = this.customRuleSelections[userId]?.[storeId];
    const initialSelected = existingCustom?.length
      ? existingCustom
      : groupName === 'Custom'
        ? []
        : roleDefaultRuleIds(groupName, this.defaultUserAccessCodes);

    const userEmail = this.colleagues[userId]?.userData?.id ?? userId;
    const storeName = this.stores[storeId] ?? storeId;
    const modal = await this.modalController.create({
      component: CustomisePermissionsModalComponent,
      componentProps: {
        contextLabel: `${userEmail} → ${storeName}`,
        initialSelected,
      },
      cssClass: ['customise-permissions-modal-css'],
      backdropDismiss: false,
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    const result = data as ICustomisePermissionsModalResult | undefined;
    if (!result?.confirmed || !result.selectedRuleIds) {
      return;
    }

    if (!this.customRuleSelections[userId]) this.customRuleSelections[userId] = {};
    this.customRuleSelections[userId][storeId] = result.selectedRuleIds;
    this.colleagueAccessGroups[userId][storeId] = 'Custom';
    this.onAccessGroupChange(userId, storeId);
  }

  private resolveAccessCodeForUserStore(userId: string, storeId: string): number {
    const groupName = this.colleagueAccessGroups[userId]?.[storeId];
    const customIds = this.customRuleSelections[userId]?.[storeId];

    if (groupName === 'Custom') {
      if (customIds?.length) {
        return accessCodeFromRuleHumanIds(customIds);
      }
      return 0;
    }

    if (groupName && customIds?.length && isCustomised(groupName, customIds, this.defaultUserAccessCodes)) {
      return accessCodeFromRuleHumanIds(customIds);
    }

    const presetCode = this.defaultUserAccessCodes[groupName]?.accessCode;
    if (presetCode !== undefined) {
      return presetCode;
    }

    return this.colleagues[userId].stores[storeId];
  }

  populateStoreUsers(): void {
    this.storeUsers = {};
    for (const userId of Object.keys(this.colleagues)) {
      const colleague = this.colleagues[userId];
      const stores = colleague.storeList;
      for (const storeId of stores) {
        if (!this.storeUsers[storeId]) this.storeUsers[storeId] = [];
        this.storeUsers[storeId].push(userId);
      }
    }

    for (const storeId of Object.keys(this.storeUsers)) {
      this.storeUsers[storeId].sort((a: string, b: string) => {
        const nameA = this.colleagues[a]?.userData?.userName.toLowerCase() || '';
        const nameB = this.colleagues[b]?.userData?.userName.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    }
  }

  getGroupNameByAccessCode(accessCode: number): string | null {
    for (const groupName of Object.keys(this.defaultUserAccessCodes ?? {})) {
      if (groupName === 'No Access' || groupName === 'Custom') {
        continue;
      }
      if (this.defaultUserAccessCodes[groupName].accessCode === accessCode) {
        return groupName;
      }
    }
    return null;
  }

  getAccessGroupToolTip(): string {
    const tooltipString: string[] = [];
    tooltipString.push('ACCESS ROLES');
    for (const groupName of this.presetRoleNames) {
      if (this.userGroupDescription[groupName]) {
        tooltipString.push(`${groupName}: ${this.userGroupDescription[groupName]}`);
      }
    }
    return tooltipString.join('\n\n');
  }

  onAccessGroupChange(userId: string, storeId: string): void {
    this.store.dispatch(setAreChangesSaved({areChangesSaved: false}));
    let groupName = this.colleagueAccessGroups[userId][storeId];

    if (groupName === undefined) {
      groupName = this.getGroupNameByAccessCode(this.colleagues[userId].stores[storeId]) ?? this.presetRoleNames[0];
      this.colleagueAccessGroups[userId][storeId] = groupName;
    }

    const accessCodeVal = this.resolveAccessCodeForUserStore(userId, storeId);
    this.colleagues[userId].stores[storeId] = accessCodeVal;

    if (!this.editedPermissions[userId]) this.editedPermissions[userId] = {};
    this.editedPermissions[userId][storeId] = {
      storeName: this.stores[storeId],
      groupName,
      groupAccessCode: accessCodeVal,
    };
    this.removePendingEditIfUnchanged(userId, storeId);
    this.syncPendingChangesFlag();
  }

  onStripUserAccessClick(userId: string, storeId: string): void {
    if (!this.deletedItems[userId]) {
      this.deletedItems[userId] = {};
    }
    this.deletedItems[userId][storeId] = this.colleagueAccessGroups[userId][storeId];
    if (this.customRuleSelections[userId]?.[storeId]) {
      if (!this.deletedCustomSelections[userId]) this.deletedCustomSelections[userId] = {};
      this.deletedCustomSelections[userId][storeId] = [...this.customRuleSelections[userId][storeId]];
      delete this.customRuleSelections[userId][storeId];
    }

    this.colleagues[userId].stores[storeId] = null as unknown as number;
    this.colleagueAccessGroups[userId][storeId] = 'No Access';

    if (!this.editedPermissions[userId]) this.editedPermissions[userId] = {};
    this.editedPermissions[userId][storeId] = {
      storeName: this.stores[storeId],
      groupName: null,
      groupAccessCode: null,
    };

    this.store.dispatch(setAreChangesSaved({areChangesSaved: false}));
    this.syncPendingChangesFlag();
  }

  onUndoDeleteClick(userId: string, storeId: string): void {
    if (this.deletedItems[userId]?.[storeId]) {
      const originalGroup = this.deletedItems[userId][storeId];
      this.colleagueAccessGroups[userId][storeId] = originalGroup;

      if (this.deletedCustomSelections[userId]?.[storeId]) {
        if (!this.customRuleSelections[userId]) this.customRuleSelections[userId] = {};
        this.customRuleSelections[userId][storeId] = [...this.deletedCustomSelections[userId][storeId]];
        delete this.deletedCustomSelections[userId][storeId];
        if (Object.keys(this.deletedCustomSelections[userId]).length === 0) {
          delete this.deletedCustomSelections[userId];
        }
      } else if (this.customRuleSelections[userId]?.[storeId]) {
        delete this.customRuleSelections[userId][storeId];
      }

      const accessCodeVal = this.resolveAccessCodeForUserStore(userId, storeId);
      this.colleagues[userId].stores[storeId] = accessCodeVal;

      if (!this.editedPermissions[userId]) this.editedPermissions[userId] = {};
      this.editedPermissions[userId][storeId] = {
        storeName: this.stores[storeId],
        groupName: originalGroup,
        groupAccessCode: accessCodeVal,
      };

      delete this.deletedItems[userId][storeId];
      if (Object.keys(this.deletedItems[userId]).length === 0) delete this.deletedItems[userId];
      this.removePendingEditIfUnchanged(userId, storeId);
    }
    this.syncPendingChangesFlag();
  }

  isDeleted(userId: string, storeId: string): boolean {
    return !!this.deletedItems[userId]?.[storeId];
  }

  sortColleagues(): void {
    const statusOrder: Record<string, number> = {['PENDING']: 1, ['VERIFIED']: 2, ['EXPIRED']: 3};

    this.colleaguesSorted = Object.keys(this.colleagues).sort((a: string, b: string) => {
      const statusA = this.colleagues[a]?.status || '';
      const statusB = this.colleagues[b]?.status || '';
      const statusComparison = (statusOrder[statusA] || 4) - (statusOrder[statusB] || 4);
      if (statusComparison !== 0) return statusComparison;

      const nameA = this.colleagues[a]?.userData?.userName.toLowerCase() || '';
      const nameB = this.colleagues[b]?.userData?.userName.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    this.fuzzyUsers = this.colleaguesSorted;
  }

  sortStores(): void {
    for (const userId of this.colleaguesSorted) {
      const colleague = this.colleagues[userId];
      const storeIds = colleague.storeList;
      this.colleaguesStoresSorted[userId] = this.sortStore(storeIds);
    }

    this.storesOrder.sort((a: string, b: string) => {
      const nameA = this.stores[a]?.toLowerCase() || '';
      const nameB = this.stores[b]?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    this.fuzzyStores = this.storesOrder;
  }

  sortStore(storeIds: string[]): string[] {
    return storeIds.sort((a: string, b: string) => {
      const nameA = this.stores[a]?.toLowerCase() || '';
      const nameB = this.stores[b]?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }

  onAccordionSelect(itemId: string): void {
    this.selectedAccordion = itemId;
  }

  onViewStoreClick(): void {
    this.byStore = true;
    this.selectedAccordion = null;
    this.reapplyPendingEdits();
  }

  onViewUserClick(): void {
    this.byStore = false;
    this.selectedAccordion = null;
    this.reapplyPendingEdits();
  }

  getUserNameInitials(item: string): string {
    return this.colleagues[item]?.userData?.userName
      ? this.colleagues[item]?.userData?.userName.charAt(0).toUpperCase()
      : '';
  }

  getLastSeenLabel(lastSeen: Date | string | undefined): string {
    return formatLastSeenDaysAgo(lastSeen);
  }

  getLastSeenDisplayText(lastSeen: Date | string | undefined): string {
    if (!lastSeen) {
      return 'Never logged in';
    }
    return `Last seen ${this.getLastSeenLabel(lastSeen)}`;
  }

  async onUserProfileClick(item: string): Promise<void> {
    const colleague = this.colleaguesCompare[item] ?? this.colleagues[item];
    if (!colleague?.userData) {
      return;
    }

    const modal = await this.modalController.create({
      component: UserProfileConfigComponent,
      componentProps: {
        otherUser: colleague.userData,
        userStatus: colleague.status,
        lastSeen: colleague.lastSeen,
      },
      cssClass: ['user-config-modal'],
      backdropDismiss: false,
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    const result = data as IUserProfileConfigDismissData | undefined;
    if (result?.confirmed && result.user?.id) {
      this.applyColleagueUserDataUpdate(result.user);
    }
  }

  private applyColleagueUserDataUpdate(update: Partial<IUser>): void {
    const userId = update.id;
    if (!userId || !this.colleagues[userId]) {
      return;
    }

    const currentUserData = this.colleagues[userId].userData;
    const mergedUserData: IUser = {
      ...currentUserData,
      ...update,
      contactDetails: {
        ...currentUserData.contactDetails,
        ...update.contactDetails,
      },
      personalDetails: {
        ...currentUserData.personalDetails,
        ...update.personalDetails,
      },
    };

    this.colleagues[userId] = {
      ...this.colleagues[userId],
      userData: mergedUserData,
    };
    this.colleaguesCompare[userId] = {
      ...this.colleaguesCompare[userId],
      userData: getDeepCopyOfObject(mergedUserData),
    };
    this.applyFilters(this.searchString);
  }

  /**
   * Opens the create-user modal. When opened from a store, `preselectedStoreIds` preselects that store.
   */
  async onAddNewUserClick(preselectedStoreIds: string[] = []): Promise<void> {
    const {storesAccessible, freeSpace} = this.getDataForAddingNewStoreOrUser();
    const modal = await this.modalController.create({
      component: AddUserComponent,
      componentProps: {
        defaultUserAccessCodes: this.defaultUserAccessCodes,
        currentUser: this.currentUser,
        userLimit: this.MAX_USER_COUNT,
        freeSpace,
        storesAccessible,
        newUser: true,
        colleaguesEmails: Object.keys(this.colleagues),
        preselectedEntryIds: preselectedStoreIds,
      },
      cssClass: ['add-user-modal'],
      backdropDismiss: false,
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    if (data?.newUserEmail) {
      this.pendingCreatedUserEmails.add(data.newUserEmail);
      this.ensurePendingColleaguePlaceholder(data.newUserEmail);
      if (data.passwordResetLink) {
        await this.presentPasswordResetLinkModal(data.passwordResetLink);
      }
    }
    if (this.byStore) {
      return;
    }
    if (data?.existingEmail) {
      this.selectedAccordion = data.existingEmail;
    } else if (data?.newUserEmail) {
      this.selectedAccordion = data.newUserEmail;
    }
  }

  private async presentPasswordResetLinkModal(passwordResetLink: IStaffAccessPasswordResetLink): Promise<void> {
    const passwordResetLinkModal = await this.modalController.create({
      component: StaffAccessPasswordResetLinkModalComponent,
      componentProps: passwordResetLink,
      cssClass: ['staff-access-password-reset-link-modal'],
      backdropDismiss: false,
    });
    await passwordResetLinkModal.present();
    await passwordResetLinkModal.onDidDismiss();
  }

  private removePendingColleaguePlaceholder(userId: string): void {
    if (!this.pendingCreatedUserEmails.has(userId)) {
      return;
    }
    this.pendingCreatedUserEmails.delete(userId);
    delete this.colleagues[userId];
    delete this.colleaguesCompare[userId];
    if (this.selectedAccordion === userId) {
      this.selectedAccordion = null;
    }
    this.sortColleagues();
    this.applyFilters(this.searchString);
  }

  private ensurePendingColleaguePlaceholder(userId: string): void {
    if (this.colleagues[userId]) {
      return;
    }

    this.colleagues[userId] = {
      features: 7,
      storeList: [],
      stores: {},
      adminAccessStores: [],
      userData: {id: userId, userName: userId, pp: ''},
      created: new Date(),
    } as IColleagueUser;

    this.sortColleagues();
    this.applyFilters(this.searchString);
  }

  async addUser(): Promise<void> {
    if (!this.hasUnassignedColleagueForSelectedStore()) {
      await this.onAddNewUserClick(this.selectedAccordion ? [this.selectedAccordion] : []);
      return;
    }

    const {storesAccessible, freeSpace} = this.getDataForAddingNewStoreOrUser(true);
    Object.keys(this.colleagues).forEach((userId: string) => {
      this.colleaguesCompare[userId].storeList.forEach((storeId: string) => {
        this.onAccessGroupChange(userId, storeId);
      });
    });

    const modal = await this.modalController.create({
      component: AddUserComponent,
      componentProps: {
        defaultUserAccessCodes: this.defaultUserAccessCodes,
        currentUser: this.currentUser,
        userLimit: this.MAX_USER_COUNT,
        freeSpace,
        storesAccessible,
        selectedEntries: this.storeUsers[this.selectedAccordion],
        currentStore: {
          storeId: this.selectedAccordion,
          storeName: this.stores[this.selectedAccordion],
        },
        currentPermissions: this.editedPermissions,
      },
      cssClass: ['add-user-modal'],
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss();
  }

  async addStore(): Promise<void> {
    const {storesAccessible, freeSpace} = this.getDataForAddingNewStoreOrUser();

    this.colleaguesCompare[this.selectedAccordion].storeList.forEach((storeId: string) => {
      this.onAccessGroupChange(this.selectedAccordion, storeId);
    });

    const modal = await this.modalController.create({
      component: AddUserComponent,
      componentProps: {
        defaultUserAccessCodes: this.defaultUserAccessCodes,
        currentUser: this.currentUser,
        userLimit: this.MAX_USER_COUNT,
        freeSpace,
        storesAccessible,
        editingUserId: this.selectedAccordion,
        selectedEntries: this.colleagues[this.selectedAccordion].storeList,
        currentPermissions: this.editedPermissions,
      },
      cssClass: ['add-user-modal'],
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss();
  }

  getDataForAddingNewStoreOrUser(
    users: boolean = false,
  ): {
    storesAccessible: { stores: Record<string, string>; order: string[] };
    freeSpace: { storeId: string; available: number }[];
  } {
    const freeSpace: { storeId: string; available: number }[] = [];
    let storesAccessible: { stores: Record<string, string>; order: string[] };

    if (users) {
      const usersMap: Record<string, string> = {};
      this.usersOrder = Object.keys(this.colleagues);

      for (const userId of this.usersOrder) {
        const userCount = this.colleagues[userId].storeList.length;
        const available = this.MAX_USER_COUNT - 1 - userCount;
        usersMap[userId] = this.colleagues[userId].userData?.userName?.trim() || userId;
        freeSpace.push({storeId: userId, available});
      }

      storesAccessible = {
        stores: usersMap,
        order: this.usersOrder,
      };
    } else {
      this.storesOrder = Object.keys(this.stores);

      for (const storeId of this.storesOrder) {
        let userCount = 0;
        for (const userId in this.colleagues) {
          const colleague = this.colleagues[userId];
          if (colleague.stores && storeId in colleague.stores) {
            userCount++;
          }
        }
        const available = this.MAX_USER_COUNT - 1 - userCount;
        freeSpace.push({storeId, available});
      }

      storesAccessible = {
        stores: this.currentUserAccess.adminAccessStores.reduce((acc, storeId) => {
          if (this.stores[storeId]) acc[storeId] = this.stores[storeId];
          return acc;
        }, {} as Record<string, string>),
        order: this.storesOrder,
      };
    }

    return {storesAccessible, freeSpace};
  }

  async save(): Promise<void> {
    const pendingChanges = this.buildPendingUserChanges();
    if (!pendingChanges.length) {
      return;
    }

    const confirmModal = await this.modalController.create({
      component: StaffAccessSaveChangesModalComponent,
      componentProps: {userChanges: pendingChanges},
      cssClass: ['staff-access-save-changes-modal-css'],
      backdropDismiss: false,
    });
    await confirmModal.present();
    const {data} = await confirmModal.onDidDismiss();
    const result = data as IStaffAccessSaveChangesModalResult | undefined;
    if (!result?.confirmed) {
      return;
    }

    for (const userId of Object.keys(this.editedPermissions)) {
      const userEdits = this.editedPermissions[userId];
      if (!userEdits || !Object.keys(userEdits).length) {
        continue;
      }

      this.store.dispatch(
        setColleaguesUserAccess({
          userId,
          editedPermissions: userEdits,
          merge: true,
          newUser: false,
        }),
      );
    }

    this.applyLocalStoreRemovals();
    this.editedPermissions = {};
    this.deletedItems = {};
    this.customRuleSelections = {};
    this.deletedCustomSelections = {};
    this.customRoleSwitchStash = {};
    this.colleagueAccessGroups = {};
    this.colleaguesCompare = getDeepCopyOfObject(this.colleagues);
    this.selectedAccordion = null;
    this.haveUsersOrStoresBeenUpdated = false;
    this.populateColleagueAccessGroups();
    this.sortStores();
    this.populateStoreUsers();
    this.store.dispatch(setAreChangesSaved({areChangesSaved: true}));
  }

  /**
   * Removes stripped stores from local colleague state after a successful save.
   */
  private applyLocalStoreRemovals(): void {
    for (const userId of Object.keys(this.deletedItems)) {
      const colleague = this.colleagues[userId];
      if (!colleague) {
        continue;
      }

      for (const storeId of Object.keys(this.deletedItems[userId])) {
        if (colleague.stores) {
          delete colleague.stores[storeId];
        }
        colleague.storeList = (colleague.storeList ?? []).filter((id: string) => id !== storeId);
        colleague.adminAccessStores = (colleague.adminAccessStores ?? []).filter((id: string) => id !== storeId);

        if (this.colleagueAccessGroups[userId]) {
          delete this.colleagueAccessGroups[userId][storeId];
        }
        if (this.customRuleSelections[userId]) {
          delete this.customRuleSelections[userId][storeId];
        }
      }
    }
  }

  discardChanges(): void {
    this.colleagues = getDeepCopyOfObject(this.colleaguesCompare);
    this.editedPermissions = {};
    this.deletedItems = {};
    this.customRuleSelections = {};
    this.deletedCustomSelections = {};
    this.customRoleSwitchStash = {};
    this.colleagueAccessGroups = {};
    this.syncPendingChangesFlag();
    this.populateColleagueAccessGroups();
    this.store.dispatch(setAreChangesSaved({areChangesSaved: true}));
  }

  private reapplyPendingEdits(): void {
    for (const userId of Object.keys(this.editedPermissions)) {
      const colleague = this.colleagues[userId];
      if (!colleague) {
        continue;
      }

      for (const storeId of Object.keys(this.editedPermissions[userId])) {
        if (this.deletedItems[userId]?.[storeId]) {
          continue;
        }

        const pending = this.editedPermissions[userId][storeId];
        if (!this.colleagueAccessGroups[userId]) {
          this.colleagueAccessGroups[userId] = {};
        }

        if (pending.groupName) {
          this.colleagueAccessGroups[userId][storeId] = pending.groupName;
        }

        if (pending.groupName === 'Custom' && pending.groupAccessCode) {
          if (!this.customRuleSelections[userId]) {
            this.customRuleSelections[userId] = {};
          }
          this.customRuleSelections[userId][storeId] = ruleHumanIdsFromAccessCode(pending.groupAccessCode);
        }

        if (pending.groupAccessCode !== null && pending.groupAccessCode !== undefined) {
          colleague.stores[storeId] = pending.groupAccessCode;
        }
      }
    }

    for (const userId of Object.keys(this.deletedItems)) {
      const colleague = this.colleagues[userId];
      if (!colleague) {
        continue;
      }

      for (const storeId of Object.keys(this.deletedItems[userId])) {
        if (!this.colleagueAccessGroups[userId]) {
          this.colleagueAccessGroups[userId] = {};
        }
        this.colleagueAccessGroups[userId][storeId] = 'No Access';
        colleague.stores[storeId] = null as unknown as number;
      }
    }
  }

  private syncPendingChangesFlag(): void {
    this.haveUsersOrStoresBeenUpdated =
      Object.keys(this.editedPermissions).some(
        (userId: string) => Object.keys(this.editedPermissions[userId] ?? {}).length > 0,
      ) || Object.keys(this.deletedItems).length > 0;
  }

  private removePendingEditIfUnchanged(userId: string, storeId: string): void {
    if (this.deletedItems[userId]?.[storeId]) {
      return;
    }

    const edit = this.editedPermissions[userId]?.[storeId];
    if (!edit) {
      return;
    }

    const savedCode = this.colleaguesCompare[userId]?.stores?.[storeId];
    const currentCode = edit.groupAccessCode;
    if (savedCode !== currentCode) {
      return;
    }

    delete this.editedPermissions[userId][storeId];
    if (Object.keys(this.editedPermissions[userId]).length === 0) {
      delete this.editedPermissions[userId];
    }
  }

  private buildPendingUserChanges(): IStaffAccessUserChange[] {
    const byUser = new Map<string, IStaffAccessUserChange>();

    for (const userId of Object.keys(this.editedPermissions)) {
      for (const storeId of Object.keys(this.editedPermissions[userId])) {
        const edit = this.editedPermissions[userId][storeId];
        const beforeCode = this.colleaguesCompare[userId]?.stores?.[storeId];
        const beforeRole = this.getAccessRoleLabel(beforeCode);

        let afterRole: string;
        let afterCode: number | null | undefined;

        if (this.deletedItems[userId]?.[storeId]) {
          afterRole = 'No Access';
          afterCode = null;
        } else {
          afterCode = edit.groupAccessCode;
          afterRole = edit.groupName === 'Custom'
            ? 'Custom'
            : (edit.groupName ?? this.getAccessRoleLabel(afterCode));
        }

        if (beforeRole === afterRole && beforeCode === afterCode) {
          continue;
        }

        if (!byUser.has(userId)) {
          byUser.set(userId, {
            userEmail: userId,
            userName: this.colleagues[userId]?.userData?.userName ?? userId,
            storeChanges: [],
          });
        }

        byUser.get(userId)?.storeChanges.push({
          storeName: this.stores[storeId] ?? this.editedPermissions[userId][storeId]?.storeName ?? storeId,
          beforeRole,
          afterRole,
        });
      }
    }

    return Array.from(byUser.values());
  }

  private getAccessRoleLabel(accessCode: number | null | undefined): string {
    if (accessCode === null || accessCode === undefined) {
      return 'No Access';
    }
    if (accessCode === 0) {
      return 'Custom';
    }

    const presetName = this.getGroupNameByAccessCode(accessCode);
    if (!presetName) {
      return 'Custom';
    }

    const decodedRuleIds = ruleHumanIdsFromAccessCode(accessCode);
    if (isCustomised(presetName, decodedRuleIds, this.defaultUserAccessCodes)) {
      return 'Custom';
    }

    return presetName;
  }

  searchUser(searchString: string): void {
    this.searchString = searchString.toLowerCase();
    this.applyFilters(this.searchString);
  }

  searchStore(searchString: string): void {
    this.storeSearchString = searchString;
    const substr: string = searchString.toLowerCase();
    if (substr === '') {
      this.fuzzyStores = this.storesOrder;
    } else {
      this.fuzzyStores = this.storesOrder.filter((storeId) => {
        const storeName = this.stores[storeId]?.toLowerCase() || '';
        return storeName.includes(substr);
      });
    }
  }

  applyFilters(query: string = ''): void {
    this.fuzzyUsers = this.colleaguesSorted.filter((userId: string) => {
      const matchesQuery =
        !query || this.colleagues[userId]?.userData?.userName.toLowerCase().includes(query.toLowerCase());
      return matchesQuery;
    });
  }

}
